import {
  applySkinPackageToProject,
  replaceSkinResourceRoots,
  type SkinApplyResult,
} from '@/features/skin-loader/runtime'
import { syncVirtualResourcePathMetadata } from '@/features/virtual-resource/rules'
import MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'
import { normalizeProjectResourceCdnUrl } from '@/features/static-resource/resourceCdn'
import { validateImportedProjectResourcePaths } from '@/features/project-mall/runtime'
import {
  cloneVirtualRoot,
  parseUiTemplatePackageFromOuiqSource,
  type ParsedOuiqTemplate,
  type ParsedUiTemplatePackage,
  type VirtualRoots,
} from './package'

function normalizeVirtualRootResourceUrls(root: DataFileMap | null): DataFileMap | null {
  const nextRoot = cloneVirtualRoot(root)
  if (!nextRoot) return null
  function walk(node: DataFileMap): void {
    if (!node.isFolder) {
      node.resourceUrl = normalizeProjectResourceCdnUrl(node.resourceUrl)
      return
    }
    for (const child of node.children) walk(child)
  }
  walk(nextRoot)
  return nextRoot
}

function normalizeVirtualRootsResourceUrls(roots: VirtualRoots): VirtualRoots {
  return {
    audio: normalizeVirtualRootResourceUrls(roots.audio),
    graphic: normalizeVirtualRootResourceUrls(roots.graphic),
    font: normalizeVirtualRootResourceUrls(roots.font),
    video: normalizeVirtualRootResourceUrls(roots.video),
  }
}

function syncVirtualRootsPathMetadata(roots: VirtualRoots): VirtualRoots {
  return {
    audio: roots.audio ? syncVirtualResourcePathMetadata('audio', roots.audio) : null,
    graphic: roots.graphic ? syncVirtualResourcePathMetadata('graphic', roots.graphic) : null,
    font: roots.font ? syncVirtualResourcePathMetadata('font', roots.font) : null,
    video: roots.video ? syncVirtualResourcePathMetadata('video', roots.video) : null,
  }
}

export function applyUiTemplatePackageToProject(params: {
  project: MainProject
  roots: VirtualRoots
  template: ParsedUiTemplatePackage
}): SkinApplyResult {
  const templateProject = params.template.project
  const templateRoots = normalizeVirtualRootsResourceUrls(params.template.roots)
  applySkinPackageToProject(params.project, {
    uiName: templateProject.UiName,
    bitmap: templateProject.Bitmap,
    width: templateProject.Width,
    height: templateProject.Height,
    entries: [],
    parsedProject: templateProject,
  })

  const replacedRoots = replaceSkinResourceRoots({
    roots: params.roots,
    incomingRoots: templateRoots,
  })
  const mergedRoots = syncVirtualRootsPathMetadata(replacedRoots)
  validateImportedProjectResourcePaths(params.project, mergedRoots.graphic)
  return { roots: mergedRoots }
}

export function applyOuiqTemplateToProject(params: {
  project: MainProject
  roots: VirtualRoots
  source: ParsedOuiqTemplate | ArrayBuffer | Uint8Array
}): SkinApplyResult {
  return applyUiTemplatePackageToProject({
    project: params.project,
    roots: params.roots,
    template: parseUiTemplatePackageFromOuiqSource(params.source),
  })
}
